function stripQuotes(s: string): string {
  return s.replace(/^["'](.*)["']$/, "$1")
}

export interface ParsedFrontmatter {
  title: string
  description: string
  category: string
  author: string
  ogTitle?: string
  ogDescription?: string
  image?: string
  /** The lead image an article page shows above its title. */
  featureImage?: string
  featureImageAlt?: string
  /** Keeps the feature image off the article page; listings still use it. */
  hideFeatureImage?: boolean
  updatedAt?: string
}

export function normalizeBlogCategory(category: string): string {
  return category === "Dispatch" ? "Changelog" : category
}

const BLOG_CATEGORY_ORDER = ["Changelog", "Company"]

/** The blog's category tabs: the leading categories first, then the rest alphabetically. */
export function orderBlogCategories(categories: Iterable<string>): string[] {
  const remaining = new Set([...categories].filter(Boolean))
  const leading = BLOG_CATEGORY_ORDER.filter((category) => remaining.delete(category))
  return [...leading, ...[...remaining].sort((a, b) => a.localeCompare(b))]
}

export interface ContentEntry {
  markdownByCategory?: Record<string, string>
  content: string
  date: string
  title: string
  description: string
  ogTitle?: string
  ogDescription?: string
}

export function parseFrontmatter(raw: string): ParsedFrontmatter {
  const match = raw.match(/^---\n([\s\S]*?)\n---/)
  if (!match) return { title: "", description: "", category: "", author: "" }
  const fm = match[1]!
  const title = stripQuotes(fm.match(/^title:\s*(.+)$/m)?.[1] ?? "")
  const description = stripQuotes(fm.match(/^description:\s*(.+)$/m)?.[1] ?? "")
  const category = stripQuotes(fm.match(/^category:\s*(.+)$/m)?.[1] ?? "")
  const author = stripQuotes(fm.match(/^author:\s*(.+)$/m)?.[1] ?? "")
  const ogTitleMatch = fm.match(/^ogTitle:\s*(.*)$/m)
  const ogDescriptionMatch = fm.match(/^ogDescription:\s*(.*)$/m)
  const ogTitle = ogTitleMatch ? stripQuotes(ogTitleMatch[1]!.trim()) : undefined
  const ogDescription = ogDescriptionMatch ? stripQuotes(ogDescriptionMatch[1]!.trim()) : undefined
  const updatedAtMatch = fm.match(/^updatedAt:[ \t]*(.*)$/m)
  const imageMatch = fm.match(/^image:\s*(.+)$/m)
  const featureImageMatch = fm.match(/^featureImage:\s*(.+)$/m)
  const featureImageAltMatch = fm.match(/^featureImageAlt:\s*(.+)$/m)
  const hideFeatureImage = /^hideFeatureImage:\s*["']?true["']?\s*$/m.test(fm)
  return {
    title,
    description,
    category,
    author,
    ogTitle,
    ogDescription,
    ...(updatedAtMatch ? { updatedAt: stripQuotes(updatedAtMatch[1]!.trim()) } : {}),
    ...(imageMatch ? { image: stripQuotes(imageMatch[1]!) } : {}),
    ...(featureImageMatch ? { featureImage: stripQuotes(featureImageMatch[1]!.trim()) } : {}),
    ...(featureImageAltMatch
      ? { featureImageAlt: stripQuotes(featureImageAltMatch[1]!.trim()) }
      : {}),
    ...(hideFeatureImage ? { hideFeatureImage } : {}),
  }
}

export function extractBody(raw: string): string {
  return raw.replace(/^---\n[\s\S]*?\n---\n*/, "").trim()
}

/** Filename (sans extension) of a legal doc's working draft, which has no effective date yet. */
export const UNRELEASED = "unreleased"

/**
 * A legal version is effective — publishable as the live document — once it is
 * a dated version (not the unreleased draft) whose date is on or before `asOf`
 * (both YYYY-MM-DD, compared lexicographically). The build loader and the
 * client resolver both apply this rule with the same build date, so every
 * surface (sitemap, llms.txt, raw .md routes, rendered pages) agrees on which
 * version is live.
 */
export function isLegalVersionEffective(filename: string, asOf: string): boolean {
  return filename !== UNRELEASED && filename <= asOf
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

/**
 * The effective date a legal version states in its "_Effective: 25 November
 * 2026_" line, as YYYY-MM-DD, or null if it has no such line. A
 * version's filename (and so its URL) is its effective date, and the build
 * rejects a version whose stated date differs.
 */
export function statedEffectiveDate(raw: string): string | null {
  const match = raw.match(/^_Effective: (\d{1,2}) ([A-Z][a-z]+) (\d{4})_$/m)
  if (!match) return null
  const month = MONTHS.indexOf(match[2]!) + 1
  if (month === 0) return null
  return `${match[3]}-${String(month).padStart(2, "0")}-${match[1]!.padStart(2, "0")}`
}
